import { useEffect, useMemo } from 'react';

import { useGetAfterCopyRightAssignmentQuery, useGetArticleContentQuery, useGetCurrentUserQuery } from 'services';
import { Card, Form } from 'antd';
import { article_order_status } from 'constant/static';
import { useMediaOfferPermission, useUserHasPermission } from 'hooks';
import { OrderDetailGrid } from 'components/ArticleOrder';
import { OrderDetailDescriptions } from 'components/ArticleOrder/OrderDetailDescriptions';
import RevertAnswerItem from 'components/RevertAnswerItem';
import AssigmentNotes from 'components/AfterCopyright/EncyclopediaUnit/AssigmentNotes';
import StatusBar from 'components/StatusBar';
import ActivityHistory from 'components/ActivityHistory';
import SupervisorOpinion from 'components/ArticleOrder/status/SupervisorOpinion';
import ArticleOrderMediaWrapper from 'components/ArticleOrderMedia/ArticleOrderMediaWrapper';

import EuPubControlDecision from './DecisionActionsArea/EuPubControlDecision';
import OwnComiteeControlDecisions from './DecisionActionsArea/OwnComiteeControlDecisions';
import EuAndPubControlDecisions from './DecisionActionsArea/EuAndPubControlDecisions';
import SupervisorControlDecision from './DecisionActionsArea/SupervisorControlDecision';
import RevertFormSupervisor from './DecisionActionsArea/RevertFormSupervisor';
import ComitteeDescriptionArea from './ComitteeDescriptionArea';
import CommiteeOpinionArea from './CommiteeOpinionArea';

export const unitDecisions = {
  review: 1,
  accept: 2,
  reject: 5,
  revert: 3,
  requestOpinion: 10,
  sendToSupervisor: 16,
  redaction: 12,
};

const AfterCopyrightContentControl = ({ offer, committee, type, encyclopediaUnit = false }) => {
  const [form] = Form.useForm();
  const { data: assigments } = useGetAfterCopyRightAssignmentQuery(
    {
      articleOffer: offer && offer.id,
      type: type,
    },
    {
      skip: !type,
    }
  );

  const { data: current_user } = useGetCurrentUserQuery();
  const { handlePermission, isArticleSupervisor } = useUserHasPermission();
  const mediaOfferPermission = useMediaOfferPermission(offer);

  /**
   * TS - İH Madde Kontrol durumunda url e owner_committee degeri gonderilmesi gerekiyor cunku bu asamada api
   * bu degeri kullaniyor, diğer durumlarda parametre olarak gelen deger kullanilabilir
   */
  const handleSupervisorDesicionComittee = useMemo(() => {
    return [article_order_status['TS - İH Madde Kontrol'], article_order_status['TS - Madde Sorumlusu Kontrol']].includes(offer.status)
      ? 'owner_committee'
      : committee;
  }, [offer.status, committee]);

  /**
   * Madde dosyalarının getirilmesi
   */
  const { data } = useGetArticleContentQuery({
    offerID: offer && offer.id,
  });

  /**
   * Karar seçeneklerinin görünürlüğü
   */
  const isShowdecisionOptions = useMemo(() => {
    return (
      (article_order_status['Ansiklopedi Birimi Kontrol'] === offer.status && (current_user.euManager || current_user.isAdmin)) ||
      (article_order_status['Yayin Mudurlugu Genel Kontrol'] === offer.status && (current_user.pubManager || current_user.isAdmin)) ||
      article_order_status['IH - Madde Sorumlusu Kontrol'] === offer.status
    );
  }, [current_user, offer.status]);

  /**
   * Madde dosyalarının set edilmesi
   */
  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        itemFile: data.contents.find((item) => item.type === 0),
        idCard: data.contents.find((item) => item.type === 1),
      });
    }
  }, [data, form]);

  // sadece gorus girilmis heyetleri filtreler
  const unitDescriptions = useMemo(() => assigments?.filter((item) => item.description !== null), [assigments]);

  return (
    <>
      <SupervisorOpinion
        isSupervisor={isArticleSupervisor(offer.articleSupervisor?.id)}
        offerID={offer.id}
      />
      <Card>
        <StatusBar status={offer && offer.status} />
        <OrderDetailGrid offer={offer} />
        <OrderDetailDescriptions
          offer={offer}
          isEncyclopediaUnitControl
        />
      </Card>
      {/**
       * Ayk control, eu control ve pub control asamasinda yollanan notlari.
       */}
      {article_order_status['TS - İH Madde Kontrol'] === offer.status && (
        <ComitteeDescriptionArea
          ownerCommiteeId={offer.ownerCommittee.id}
          aykControlDescription={offer.aykControlDescription}
          euControlDescription={offer.euControlDescription}
          pubControlDescription={offer.pubControlDescription}
          ownerCommitteeOpinion={offer.ownerCommitteeOpinion}
        />
      )}
      <ActivityHistory
        id={offer && offer.id}
        requestType="post_offer"
      />
      {unitDescriptions?.length > 0 && <AssigmentNotes descriptions={unitDescriptions} />}
      <ArticleOrderMediaWrapper
        offerID={offer && offer.id}
        permission={mediaOfferPermission}
        isEncylopediaUnitControl
        isCommiteeControlColumnsVisible={!mediaOfferPermission.isReadOnly}
        isContentSend
      />
      {offer?.revertReasons?.ayk_control?.length > 0 && (
        <Card>
          <h3>Ansiklopedi birimi aşağıdaki bilgiler doğrultusunda görüşünüzü talep etti.</h3>
          <Card>
            {offer?.revertReasons?.ayk_control.map((item, index) => (
              <RevertAnswerItem
                key={index}
                title={item.revertReason.title}
                answer={item.answer}
              />
            ))}
          </Card>
        </Card>
      )}
      <CommiteeOpinionArea ownerCommitteeOpinion={offer.ownerCommitteeOpinion} />
      {isShowdecisionOptions && (
        <EuAndPubControlDecisions
          step={offer.step}
          committee={committee}
          offerId={offer.id}
          encyclopediaUnit={encyclopediaUnit}
        />
      )}
      {article_order_status['Ansiklopedi Yonetim Kurulu Kontrol'] === offer.status && (
        <EuPubControlDecision
          step={offer.step}
          committee={committee}
          offerId={offer.id}
        />
      )}
      {/**
       * Sorumlu heyetin maddeyi kabul veya madde sorumlusuna gondermesi icin gereken alana ulasmasini saglar
       */}
      {handlePermission(offer && offer.ownerCommittee.id) && article_order_status['TS - İH Madde Kontrol'] === offer.status && (
        <OwnComiteeControlDecisions
          step={offer.step}
          committee={handleSupervisorDesicionComittee}
          offerId={offer.id}
        />
      )}
      {/**
       * Madde sorumlusunun maddeyi gondermesi icin gereken alana ulasmasini saglayars
       */}
      {article_order_status['TS - Madde Sorumlusu Kontrol'] === offer.status && isArticleSupervisor(offer.articleSupervisor?.id) && (
        <SupervisorControlDecision
          step={offer.step}
          committee={handleSupervisorDesicionComittee}
          offerId={offer.id}
        />
      )}
      {/*
       * Heytetin maddeyi madde sorumlusundangeri cekmesi icin gereken alana ulasmasini saglar
       */}
      {article_order_status['TS - Madde Sorumlusu Kontrol'] === offer.status &&
        handlePermission(offer && offer.ownerCommittee.id) &&
        !isArticleSupervisor(offer.articleSupervisor?.id) && <RevertFormSupervisor offerId={offer.id} />}
    </>
  );
};

export default AfterCopyrightContentControl;
